import React, { useEffect, useState } from 'react'
import { useFormik } from 'formik'
import * as yup from 'yup'
import { TextArea } from 'components'
import professionalNPSAnalytics from 'services/analytics/ProfessionalNPS/professionalNPSAnalytics'
import { translate } from 'locales'
import { alertDanger } from 'utils/toast'
import {
  createDoctorNps,
  dismissDoctorNps,
  getCurrentDoctorId,
  setProfessionalNpsClosedThisSession,
} from './services'
import * as S from './styles'

export interface ProfessionalNPSProps {
  onClose: () => void
}

const NPS_SCALE = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10] as const

const schemaNPS = yup.object().shape({
  score: yup
    .number()
    .min(0, translate('professionalNps.errors.selectScore'))
    .max(10, translate('professionalNps.errors.selectScore'))
    .nullable(),
  reasonForScore: yup.string().optional(),
  improvementSuggestion: yup.string().optional(),
})

function CloseIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M15 5L5 15M5 5l10 10"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

export function ProfessionalNPS({ onClose }: ProfessionalNPSProps) {
  const [showThanksScreen, setShowThanksScreen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isDismissing, setIsDismissing] = useState(false)

  useEffect(() => {
    professionalNPSAnalytics.trackSurveyView()
  }, [])

  useEffect(() => {
    if (showThanksScreen) {
      professionalNPSAnalytics.trackThanksView()
    }
  }, [showThanksScreen])

  const formik = useFormik({
    initialValues: {
      score: null as number | null,
      reasonForScore: '',
      improvementSuggestion: '',
    },
    validationSchema: schemaNPS,
    onSubmit: async (values) => {
      const doctorId = getCurrentDoctorId()
      if (!doctorId || values.score === null) {
        alertDanger(translate('professionalNps.errors.submitFailed'))
        return
      }

      setIsSubmitting(true)
      professionalNPSAnalytics.trackSubmit({
        score: values.score,
        hasReason: values.reasonForScore.trim().length > 0,
        hasImprovementSuggestion: values.improvementSuggestion.trim().length > 0,
      })

      try {
        await createDoctorNps({
          doctor_id: doctorId,
          stars: values.score,
          comment: values.reasonForScore.trim(),
          suggestion: values.improvementSuggestion.trim(),
        })
        setShowThanksScreen(true)
      } catch {
        alertDanger(translate('professionalNps.errors.submitFailed'))
        // Não marca como enviado; a pesquisa voltará a aparecer na próxima sessão
      } finally {
        setIsSubmitting(false)
      }
    },
  })

  const handleClose = (
    source: 'survey' | 'thanks',
    action: 'close_button' = 'close_button'
  ) => {
    professionalNPSAnalytics.trackClose(source, action)
    const doctorId = getCurrentDoctorId()
    setProfessionalNpsClosedThisSession(doctorId)
    onClose()
  }

  const handleSkip = async () => {
    professionalNPSAnalytics.trackSkip()
    const doctorId = getCurrentDoctorId()
    if (doctorId) {
      try {
        setIsDismissing(true)
        await dismissDoctorNps(doctorId)
      } catch {
        // Silent fail so closing still works.
      } finally {
        setIsDismissing(false)
      }
    }
    professionalNPSAnalytics.trackClose('survey', 'dismiss_button')
    onClose()
  }

  const isScoreSelected = formik.values.score !== null

  if (showThanksScreen) {
    return (
      <S.ThanksWrapper data-testid="professional-nps-thanks">
        <S.CloseRow data-testid="professional-nps-thanks-close-row">
          <S.CloseButton
            type="button"
            onClick={async () => {
              handleClose('thanks', 'close_button')
            }}
            aria-label={translate('professionalNps.actions.close')}
            data-testid="professional-nps-thanks-close-icon"
          >
            <CloseIcon />
          </S.CloseButton>
        </S.CloseRow>
        <S.ThanksTitleBlock data-testid="professional-nps-thanks-content">
          <S.ThanksTitle data-testid="professional-nps-thanks-title">
            {translate('professionalNps.thanks.title')}
          </S.ThanksTitle>
          <S.ThanksSubtitle data-testid="professional-nps-thanks-subtitle">
            {translate('professionalNps.thanks.subtitle')}
          </S.ThanksSubtitle>
        </S.ThanksTitleBlock>
        <S.ThanksButtons data-testid="professional-nps-thanks-actions">
          <S.ThanksCloseButton
            type="button"
            onClick={async () => {
              handleClose('thanks', 'close_button')
            }}
            data-testid="professional-nps-thanks-close-button"
          >
            {translate('professionalNps.actions.close')}
          </S.ThanksCloseButton>
        </S.ThanksButtons>
      </S.ThanksWrapper>
    )
  }

  return (
    <S.Wrapper data-testid="professional-nps-survey">
      <S.CloseRow data-testid="professional-nps-survey-close-row">
        <S.CloseButton
          type="button"
          onClick={async () => {
            handleClose('survey', 'close_button')
          }}
          aria-label={translate('professionalNps.actions.close')}
          data-testid="professional-nps-survey-close-icon"
        >
          <CloseIcon />
        </S.CloseButton>
      </S.CloseRow>
      <S.TitleBlock data-testid="professional-nps-survey-header">
        <S.Title data-testid="professional-nps-survey-title">
          {translate('professionalNps.title')}
        </S.Title>
      </S.TitleBlock>

      <S.Options onSubmit={formik.handleSubmit} data-testid="professional-nps-form">
        <S.Question data-testid="professional-nps-question">
          {translate('professionalNps.question')}
        </S.Question>

        <S.ScaleRow data-testid="professional-nps-scale">
          {NPS_SCALE.map((value) => (
            <S.ScaleOption
              key={value}
              type="button"
              $selected={formik.values.score === value}
              onClick={() => {
                formik.setFieldValue('score', value)
                professionalNPSAnalytics.trackScoreSelected(value)
              }}
              aria-pressed={formik.values.score === value}
              aria-label={`${translate('professionalNps.score.ariaLabel')} ${value}`}
              data-testid={`professional-nps-score-${value}`}
              data-score={value}
            >
              {value}
            </S.ScaleOption>
          ))}
        </S.ScaleRow>
        <S.ScaleLabels data-testid="professional-nps-scale-labels">
          <S.LabelLeft data-testid="professional-nps-label-left">
            {translate('professionalNps.score.leftLabel')}
          </S.LabelLeft>
          <S.LabelRight data-testid="professional-nps-label-right">
            {translate('professionalNps.score.rightLabel')}
          </S.LabelRight>
        </S.ScaleLabels>

        <S.FieldGroup data-testid="professional-nps-reason-field">
          <S.FieldLabel htmlFor="reasonForScore">
            {translate('professionalNps.fields.reason.label')}
          </S.FieldLabel>
          <S.TextareaWrap data-testid="professional-nps-reason-textarea">
            <TextArea
              name="reasonForScore"
              placeholder={translate('professionalNps.fields.reason.placeholder')}
              value={formik.values.reasonForScore}
              onChange={formik.handleChange}
              rows={3}
            />
          </S.TextareaWrap>
        </S.FieldGroup>

        <S.FieldGroup data-testid="professional-nps-improvement-field">
          <S.FieldLabel htmlFor="improvementSuggestion">
            {translate('professionalNps.fields.suggestion.label')}
          </S.FieldLabel>
          <S.TextareaWrap data-testid="professional-nps-improvement-textarea">
            <TextArea
              name="improvementSuggestion"
              placeholder={translate(
                'professionalNps.fields.suggestion.placeholder'
              )}
              value={formik.values.improvementSuggestion}
              onChange={formik.handleChange}
              rows={3}
            />
          </S.TextareaWrap>
        </S.FieldGroup>

        <S.Buttons data-testid="professional-nps-actions">
          <S.SurveyOutlineButton
            type="button"
            onClick={async () => {
              await handleSkip()
            }}
            disabled={isDismissing}
            data-testid="professional-nps-skip-button"
          >
            {translate('professionalNps.actions.skip')}
          </S.SurveyOutlineButton>
          <S.SurveyPrimaryButton
            type="submit"
            disabled={!isScoreSelected || isSubmitting}
            data-testid="professional-nps-submit-button"
          >
            {translate('professionalNps.actions.submit')}
          </S.SurveyPrimaryButton>
        </S.Buttons>
      </S.Options>
    </S.Wrapper>
  )
}
